import { __awaiter, __generator } from "tslib";
import { getFileTypeFromFileName, getReferralFromFileType, } from '@office-start/file-utility';
import { cancelUploadSession } from '@office-start/graph-service';
import { logClickEvent } from '@office-start/instrumentation-service';
import { KnownInstrumentationEvents, } from '@office-start/instrumentation-types';
import { logUserAction } from '@office-start/telemetry-service';
import { getReferral } from '@office-start/telemetry-utility';
import { UploadErrorType, UploadSessionState, UploadSource, UploadStatus, } from '@office-start/upload-types';
import { BehaviorSubject } from 'rxjs';
import { isFinished } from '../isFinished';
import { UploadResult } from '../models/UploadResult';
import { progressState } from '../progressState';
import { uploadLargeFile } from '../uploadLargeFile';
import { getUploadDestinationDriveID, getUploadDestinationPath, logFeatureForUploadHelper, } from '../utilities';
export var executeLargeUploadFlow = function (_a) {
    var fileUploadState = _a.fileUploadState, updateFileUploadState = _a.updateFileUploadState, graphXhrOptions = _a.graphXhrOptions, uploadConfig = _a.uploadConfig, componentContext = _a.componentContext;
    return __awaiter(void 0, void 0, void 0, function () {
        var bytesLoaded, sessionId, uploadStartTime, path, driveId, uploadSession, fileType, clickLogged, elementId, referralType, logClickHelper, uploadObservable, uploadComplete;
        return __generator(this, function (_b) {
            if (!graphXhrOptions || !uploadConfig || !componentContext) {
                return [2 /*return*/, Promise.reject('Missing configs')];
            }
            if (!fileUploadState || !fileUploadState.file) {
                return [2 /*return*/, Promise.reject('Missing file')];
            }
            bytesLoaded = 0;
            sessionId = componentContext.sessionId || '';
            uploadStartTime = performance.now();
            path = getUploadDestinationPath(fileUploadState);
            driveId = getUploadDestinationDriveID(fileUploadState);
            uploadSession = {
                sessionId: sessionId,
                file: fileUploadState.file,
                uploadSource: fileUploadState.telemetry.source,
                cancellationToken: fileUploadState.cancellationToken,
                bytesUploaded: 0,
                state: UploadSessionState.Started,
                startTime: uploadStartTime,
                progress: new BehaviorSubject(progressState(bytesLoaded, fileUploadState.file.size)),
                fileSize: fileUploadState.file.size,
                lastLoaded: bytesLoaded,
                path: path,
                driveId: driveId,
            };
            fileType = getFileTypeFromFileName(fileUploadState.file.name);
            clickLogged = false;
            elementId = UploadSource[fileUploadState.telemetry.source];
            referralType = (uploadConfig === null || uploadConfig === void 0 ? void 0 : uploadConfig.uploadFileTypes.has(fileType))
                ? getReferralFromFileType(fileType)
                : "Documents" /* KnownWorkloadReferralNames.OneDrive */;
            logClickHelper = function (success) {
                if (clickLogged) {
                    return;
                }
                clickLogged = true;
                logClick({
                    elementId: elementId,
                    sessionId: sessionId,
                    uploadSource: fileUploadState.telemetry.source,
                    _fileType: fileType,
                    success: success,
                    area: fileUploadState.telemetry.area,
                    referralType: referralType,
                });
            };
            uploadObservable = new BehaviorSubject(uploadSession);
            uploadSession.progress.subscribe(function (progress) {
                if (updateFileUploadState &&
                    fileUploadState &&
                    fileUploadState.sequenceNumber) {
                    updateFileUploadState({
                        sequenceNumber: fileUploadState.sequenceNumber,
                        progress: progress.loaded / progress.total,
                        uploadStatus: UploadStatus.Uploading,
                    });
                }
            });
            uploadComplete = false;
            uploadObservable.subscribe(function (session) {
                if (session.state === UploadSessionState.Completed) {
                    if (updateFileUploadState &&
                        fileUploadState &&
                        fileUploadState.sequenceNumber) {
                        updateFileUploadState({
                            sequenceNumber: fileUploadState.sequenceNumber,
                            progress: 1,
                            uploadStatus: UploadStatus.Completed,
                        });
                    }
                    uploadComplete = true;
                    logClickHelper(true); // TODO: do this after successfully opened file
                    // Logs telemetry when upload of large file is successfully complete
                    logFeatureForUploadHelper(sessionId, UploadResult.Success, // Logs 'IsUploadSuccessful' as 'true' if upload of large file is successful
                    uploadStartTime, fileUploadState);
                }
                else if (session.state === UploadSessionState.Cancelled) {
                    uploadObservable.unsubscribe();
                    if (!uploadComplete) {
                        if (updateFileUploadState &&
                            fileUploadState &&
                            fileUploadState.sequenceNumber) {
                            updateFileUploadState({
                                sequenceNumber: fileUploadState.sequenceNumber,
                                progress: 1,
                                uploadStatus: UploadStatus.Cancelled,
                            });
                        }
                        logClickHelper(false);
                        // Logs telemetry when upload is cancelled
                        logFeatureForUploadHelper(sessionId, UploadResult.Cancelled, // Logs 'IsCancelled' as 'true' if upload of large file has been cancelled
                        uploadStartTime, fileUploadState);
                    }
                    else {
                        if (updateFileUploadState &&
                            fileUploadState &&
                            fileUploadState.sequenceNumber) {
                            updateFileUploadState({
                                sequenceNumber: fileUploadState.sequenceNumber,
                                progress: 1,
                                uploadStatus: UploadStatus.Completed,
                            });
                        }
                    }
                }
                else if (session.state === UploadSessionState.Failed) {
                    if (updateFileUploadState &&
                        fileUploadState &&
                        fileUploadState.sequenceNumber) {
                        updateFileUploadState({
                            sequenceNumber: fileUploadState.sequenceNumber,
                            progress: 1,
                            uploadStatus: UploadStatus.Failed,
                            error: session.error,
                        });
                    }
                    logClickHelper(false);
                    // Logs telemetry when upload failed
                    logFeatureForUploadHelper(sessionId, UploadResult.Failed, // Logs 'IsUploadSuccessful' as 'false' if upload of large file has failed
                    uploadStartTime, fileUploadState);
                }
            });
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            uploadSession.cancellationToken.onCancel(function () {
                if (uploadSession.sessionUrl && !isFinished(uploadSession.state)) {
                    cancelUploadSession(graphXhrOptions, uploadSession.sessionUrl, uploadSession.sessionId);
                }
                uploadSession.state = UploadSessionState.Cancelled;
                uploadObservable.next(uploadSession);
            });
            return [2 /*return*/, new Promise(function (resolve, reject) {
                    uploadLargeFile(graphXhrOptions, uploadSession, uploadObservable, uploadConfig).then(function () {
                        if (uploadSession.result) {
                            fileUploadState.fileUrlModel = uploadSession.result;
                            fileUploadState.progress = 1;
                            fileUploadState.uploadStatus = UploadStatus.Completed;
                            resolve(fileUploadState);
                        }
                        else {
                            reject(UploadErrorType.Unknown);
                        }
                    });
                })];
        });
    });
};
function logClick(_a) {
    var elementId = _a.elementId, sessionId = _a.sessionId, uploadSource = _a.uploadSource, _fileType = _a._fileType, success = _a.success, area = _a.area, referralType = _a.referralType, platform = _a.platform;
    var clickData = getClickDataForId({ elementId: elementId, sessionId: sessionId, uploadSource: uploadSource });
    logClickEvent(clickData.eventName, elementId, area, success ? referralType : '', platform, clickData.params, clickData.metrics);
    // TODO: ADD Error case
    if (success) {
        logUserAction({
            eventName: clickData.eventName,
            id: elementId,
            area: area,
            result: 'Refer',
            target: getReferral(referralType),
        });
    }
}
function getClickDataForId(_a) {
    var elementId = _a.elementId, sessionId = _a.sessionId, uploadSource = _a.uploadSource, buttonOrigin = _a.buttonOrigin, numDocuments = _a.numDocuments;
    var params = {};
    params["UploadSessionId" /* InstrumentationParam.UploadSessionId */] = sessionId;
    elementId = buttonOrigin ? "".concat(elementId, "-").concat(buttonOrigin) : elementId;
    params["ElementId" /* InstrumentationParam.ElementId */] = elementId;
    params["ItemCount" /* InstrumentationParam.ItemCount */] = (numDocuments || 0).toString();
    params["ItemCategory" /* InstrumentationParam.ItemCategory */] = UploadSource[uploadSource];
    return {
        eventName: KnownInstrumentationEvents.UploadClick,
        params: params,
        metrics: [],
    };
}
//# sourceMappingURL=executeLargeUploadFlow.js.map